<script setup lang="ts">
import { computed, ref } from 'vue'
import { Color } from '@previo/codebooks'
import PBtn from '../btn/PBtn.vue'
import { PBtnType } from '../btn/types'
import PIcon from '../icon/PIcon.vue'
import { PMaxLines } from '../typography/types'
import { PSnackbarType } from '../snackbar/types'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextStandard from '../typography/body/PTextStandard.vue'
import PTextDescription from '../typography/system/PTextDescription.vue'
import PTextLink from '../typography/links/PTextLink.vue'

type Emits = {
  (e: 'hide', id: null | string): void
  (e: 'link', evt: Event): void
}

const emits = defineEmits<Emits>()

type Props = {
  type: PSnackbarType
  title?: null | string
  titleMaxLines?: null | PMaxLines
  description?: string | null
  icon?: null | string
  btnText?: null | string
  btnType?: PBtnType
  btnIconEnd?: null | string
  btnCallback?: null | any
  closable?: boolean
  id?: null | string
  linkText?: null | string
  disable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  closable: false,
  disable: false,
  titleMaxLines: null,
  btnCallback: () => {},
})

const bgColor = computed<Color>(() => {
  switch (props.type) {
    case PSnackbarType.POSITIVE:
      return Color.POSITIVE_TRANSPARENT
    case PSnackbarType.WARNING:
      return Color.WARNING_TRANSPARENT
    case PSnackbarType.NEGATIVE:
      return Color.NEGATIVE_TRANSPARENT
    case PSnackbarType.INFO:
      return Color.INFO_TRANSPARENT

    default:
      return Color.INFO_TRANSPARENT
  }
})

const color = computed<Color>(() => {
  switch (props.type) {
    case PSnackbarType.POSITIVE:
      return Color.POSITIVE
    case PSnackbarType.WARNING:
      return Color.WARNING
    case PSnackbarType.NEGATIVE:
      return Color.NEGATIVE_STATE
    case PSnackbarType.INFO:
      return Color.INFO

    default:
      return Color.INFO
  }
})

const btnColor = computed<Color.PRIMARY | Color.POSITIVE | Color.NEGATIVE_STATE>(() => {
  switch (props.type) {
    case PSnackbarType.POSITIVE:
      return Color.POSITIVE
    case PSnackbarType.NEGATIVE:
      return Color.NEGATIVE_STATE

    default:
      return Color.PRIMARY
  }
})

const isVisible = ref(true)
const isBtnVisible = computed<boolean>(() => {
  return (!!props.btnText || !!props.btnIconEnd) && !!props.disable === false
})

export interface HideProps {
  id: string
}

const onHideClick = (): void => {
  isVisible.value = false
  emits('hide', props.id)
}

const onBtnClick = (): void => {
  if (!props.btnCallback) {
    return
  }

  props.btnCallback()
}
</script>

<template>
  <div v-if="isVisible" :class="`bg-${bgColor} p-generic-borderRadius ds-snackbar`">
    <div class="p-flex p-no-wrap p-content-center p-items-center p-justify-between">
      <div class="p-flex p-no-wrap p-content-center p-items-center">
        <template v-if="icon">
          <p-icon class="p-self-center" :icon="icon" :color="color" />
          <p-space :mobile="PSpaceSize.SM" />
        </template>

        <div>
          <p-text-standard :text="title" :color="color" :max-lines="titleMaxLines" />
          <slot name="description">
            <p-text-description v-if="description" :text="description" :color="Color.GREY_500" />
          </slot>

          <template v-if="linkText">
            <p-space :mobile="PSpaceSize.XS" vertical />
            <p-text-link
              class="p-cursor-pointer"
              :text="linkText"
              :color="Color.PRIMARY"
              underline
              @click="(e: Event) => emits('link', e)"
            />
          </template>
        </div>
      </div>

      <div class="p-flex p-items-center p-no-wrap">
        <p-space :mobile="PSpaceSize.SM" />
        <p-btn
          v-if="isBtnVisible"
          :type="PBtnType.TERTIARY"
          :text="btnText"
          :icon-end="btnIconEnd"
          :color="btnColor"
          cy="action"
          @click="onBtnClick"
        />

        <p-space v-if="closable" :mobile="PSpaceSize.SM" />

        <p-icon
          v-if="closable"
          icon="close"
          :color="color"
          class="p-cursor-pointer"
          @click="onHideClick"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/variables/ds.spacing';

.ds-snackbar {
  padding: $space-sm-mobile;
}
</style>
